import { observer } from 'mobx-react-lite'
import React from 'react'
import { Alert, Button, DatePicker, InputNumber, Skeleton, Space, Tooltip } from 'antd'
import {
    QuestionCircleOutlined,
    SendOutlined,
    MobileOutlined,
    AimOutlined,
    ShoppingCartOutlined,
    WalletOutlined,
    ExperimentOutlined,
} from '@ant-design/icons'
import i from '../../../translations/i'
import { getPrice } from '../../../helpers/price'
import { storeLocal } from '../../../helpers/stores'
import { pushesFunnel, PushFunnelStats, PUSH_FUNNEL_ENABLED } from './push_funnel.controller'
import './pushes.less'

type Funnel = typeof pushesFunnel

const EMPTY = '—'

const locale = () => {
    const lang = storeLocal.get('lang') || process.env.REACT_APP_DEFAULT_LANGUAGE || 'en'
    return lang === 'ua' ? 'uk' : lang
}

/** Дата начала сбора статистики воронки: «17 октября 2026 г., 12:00» в языке админки */
export const formatSince = (timestamp: number) =>
    new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        .format(new Date(timestamp * 1000))

export const percent = (value: number | null | undefined) => value === null || value === undefined ? EMPTY : `${value.toLocaleString()}%`

export const Hint = ({ title, hint }: { title: React.ReactNode, hint: string }) => (
    <Space size={4}>
        {title}
        <Tooltip title={hint}>
            <QuestionCircleOutlined style={{ color: 'var(--text-muted-color)' }} />
        </Tooltip>
    </Space>
)

const Step = ({ icon, tone, label, value, rate, extra }: {
    icon: React.ReactNode
    tone: string
    label: React.ReactNode
    value: React.ReactNode
    rate?: React.ReactNode
    extra?: React.ReactNode
}) => (
    <div className="pu-step">
        <div className="pu-step__top">
            <span className={`pu-step__icon pu-tone--${tone}`}>{icon}</span>
            {rate}
        </div>
        <div className={`pu-step__value${typeof value === 'string' && value.length > 9 ? ' pu-step__value--long' : ''}`} title={typeof value === 'string' ? value : undefined}>{value}</div>
        <div className="pu-step__label">{label}</div>
        {extra && <div className="pu-step__extra">{extra}</div>}
    </div>
)

// Ширина полосы — доля от доставленных; ненулевое значение всегда видно хотя бы тонкой полоской
const barWidth = (value: number, base: number) => {
    if (!base || !value) return 0
    return Math.max(1.5, Math.min(100, value / base * 100))
}

const Bar = ({ label, value, width, kind, caption }: { label: string, value: React.ReactNode, width: number, kind: string, caption?: string }) => (
    <div className="pu-bar">
        <span className="pu-bar__label">{label}</span>
        <div className="pu-bar__track">
            <div className={`pu-bar__fill pu-bar__fill--${kind}`} style={{ width: `${width}%` }} />
        </div>
        <span className="pu-bar__value">{value}{caption ? <span className="pu-muted"> · {caption}</span> : null}</span>
    </div>
)

/**
 * Витрина «скоро», пока воронка в разработке: видно, что функция есть и что она покажет,
 * но без цифр, запросов и фильтров
 */
const PushFunnelSoon = ({ funnel }: { funnel: Funnel }) => (
    <section className="pu-funnel pu-funnel--soon" aria-disabled="true">
        <div className="pu-funnel__head">
            <div>
                <h2 className="pu-funnel__title">
                    {i.t('pushes:funnel.title')}
                    <span className="pu-badge pu-badge--soon">{i.t('pushes:funnel.soon')}</span>
                </h2>
                <div className="pu-funnel__subtitle">{i.t('pushes:funnel.subtitle')}</div>
            </div>
            <div className="pu-funnel__controls">
                <DatePicker.RangePicker format="DD.MM.YYYY" value={funnel.period} disabled />
            </div>
        </div>

        <div className="pu-funnel__notice">
            <span className="pu-funnel__notice-icon"><ExperimentOutlined /></span>
            <div>
                <div className="pu-funnel__notice-title">{i.t('pushes:funnel.soonTitle')}</div>
                <div>{i.t('pushes:funnel.soonText')}</div>
            </div>
        </div>

        <div className="pu-funnel__preview">
            <div className="pu-funnel__steps">
                <Step icon={<SendOutlined />} tone="violet" label={funnel.source === 'pushes' ? i.t('pushes:funnel.sends_pushes') : i.t('pushes:funnel.sends_templates')} value={EMPTY} />
                <Step icon={<MobileOutlined />} tone="blue" label={i.t('pushes:funnel.delivered')} value={EMPTY} />
                <Step icon={<AimOutlined />} tone="cyan" label={i.t('pushes:funnel.taps')} value={EMPTY} />
                <Step icon={<ShoppingCartOutlined />} tone="green" label={i.t('pushes:funnel.orders')} value={EMPTY} />
                <Step icon={<WalletOutlined />} tone="orange" label={i.t('pushes:funnel.revenue')} value={EMPTY} />
            </div>

            <div className="pu-funnel__bars">
                <Bar label={i.t('pushes:funnel.delivered')} kind="delivered" width={100} value="" />
                <Bar label={i.t('pushes:funnel.taps')} kind="taps" width={38} value="" />
                <Bar label={i.t('pushes:funnel.orders')} kind="orders" width={12} value="" />
            </div>
        </div>
    </section>
)

/** Итоги воронки за период: шаги, полосы воронки, выбор периода и окно атрибуции */
export const PushFunnelSummary = observer(({ funnel }: { funnel: Funnel }) => {
    if (!PUSH_FUNNEL_ENABLED) return <PushFunnelSoon funnel={funnel} />

    const total = funnel.total
    const value = (render: (stats: PushFunnelStats) => React.ReactNode) => total ? render(total) : EMPTY

    const controls = (
        <div className="pu-funnel__controls">
            <Tooltip title={i.t('pushes:funnel.periodHint', { timezone: funnel.timezone || 'UTC' })}>
                <DatePicker.RangePicker
                    format="DD.MM.YYYY"
                    allowClear={false}
                    value={funnel.period}
                    onChange={(dates: any) => funnel.setPeriod(dates)}
                />
            </Tooltip>
        </div>
    )

    return (
        <section className="pu-funnel">
            <div className="pu-funnel__head">
                <div>
                    <h2 className="pu-funnel__title">
                        {i.t('pushes:funnel.title')}
                        <span className="pu-badge pu-badge--new">{i.t('pushes:funnel.new')}</span>
                    </h2>
                    <div className="pu-funnel__subtitle">{i.t('pushes:funnel.subtitle')}</div>
                </div>
                {controls}
            </div>

            {!funnel.available
                ? <Alert type="info" showIcon message={i.t('pushes:funnel.unavailable')} />
                : funnel.loading && !total
                    ? <Skeleton active paragraph={{ rows: 4 }} />
                    : <>
                        <div className="pu-funnel__steps">
                            <Step
                                icon={<SendOutlined />}
                                tone="violet"
                                label={funnel.source === 'pushes' ? i.t('pushes:funnel.sends_pushes') : i.t('pushes:funnel.sends_templates')}
                                value={value(stats => stats.sends.toLocaleString())}
                            />
                            <Step
                                icon={<MobileOutlined />}
                                tone="blue"
                                label={<Hint title={i.t('pushes:funnel.delivered')} hint={i.t('pushes:funnel.deliveredHint')} />}
                                value={value(stats => stats.delivered.toLocaleString())}
                            />
                            <Step
                                icon={<AimOutlined />}
                                tone="cyan"
                                label={<Hint title={i.t('pushes:funnel.taps')} hint={i.t('pushes:funnel.tapsHint')} />}
                                value={value(stats => stats.taps.toLocaleString())}
                                rate={total && total.ctr !== null
                                    ? <Tooltip title={i.t('pushes:funnel.ctrHint')}><span className="pu-rate">{i.t('pushes:funnel.ctr')} {percent(total.ctr)}</span></Tooltip>
                                    : null}
                            />
                            <Step
                                icon={<ShoppingCartOutlined />}
                                tone="green"
                                label={<Hint title={i.t('pushes:funnel.orders')} hint={i.t('pushes:funnel.ordersHint')} />}
                                value={value(stats => stats.orders.toLocaleString())}
                                rate={total && total.conversion !== null
                                    ? <Tooltip title={i.t('pushes:funnel.conversionHint')}><span className="pu-rate">{percent(total.conversion)}</span></Tooltip>
                                    : null}
                                extra={total && total.canceled > 0 ? i.t('pushes:funnel.canceled', { value: total.canceled }) : null}
                            />
                            <Step
                                icon={<WalletOutlined />}
                                tone="orange"
                                label={i.t('pushes:funnel.revenue')}
                                value={value(stats => getPrice(stats.revenue))}
                            />
                        </div>

                        {total && total.sends === 0 &&
                            <Alert
                                style={{ marginTop: 16 }}
                                type="info"
                                showIcon
                                message={typeof funnel.trackingSince === 'number' && funnel.period[1].endOf('day').unix() < funnel.trackingSince
                                    ? i.t('pushes:funnel.emptyBeforeStart', { date: formatSince(funnel.trackingSince) })
                                    : i.t('pushes:funnel.emptyPeriod')
                                }
                            />
                        }

                        {total && total.delivered > 0 &&
                            <div className="pu-funnel__bars">
                                <Bar label={i.t('pushes:funnel.delivered')} kind="delivered" width={100} value={total.delivered.toLocaleString()} />
                                <Bar
                                    label={i.t('pushes:funnel.taps')}
                                    kind="taps"
                                    width={barWidth(total.taps, total.delivered)}
                                    value={total.taps.toLocaleString()}
                                    caption={total.ctr !== null ? percent(total.ctr) : undefined}
                                />
                                <Bar
                                    label={i.t('pushes:funnel.orders')}
                                    kind="orders"
                                    width={barWidth(total.orders, total.delivered)}
                                    value={total.orders.toLocaleString()}
                                    caption={total.conversion !== null ? percent(total.conversion) : undefined}
                                />
                            </div>
                        }

                        <div className="pu-funnel__footer">
                            <span>{i.t('pushes:funnel.attributionBefore')}</span>
                            <InputNumber
                                id="push-attribution-hours"
                                size="small"
                                min={1}
                                max={720}
                                precision={0}
                                value={funnel.attributionHours}
                                onChange={(hours) => funnel.setAttributionHours(hours)}
                                style={{ width: 80 }}
                            />
                            <Hint title={i.t('pushes:funnel.attributionAfter')} hint={i.t('pushes:funnel.attributionHoursHint')} />
                            {funnel.attributionHours !== funnel.savedAttributionHours &&
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={funnel.savingHours}
                                    onClick={() => funnel.saveAttributionHours()}
                                >
                                    {i.t('def:actions.save')}
                                </Button>
                            }
                        </div>
                    </>
            }
        </section>
    )
})
